import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(theme => ({
  // Layout
  urlBar: {
    background: '#16213e',
    padding: '14px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    borderBottom: '1px solid #0f3460',
  },
  urlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  urlBarLabel: {
    color: '#94a3b8',
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  urlInput: {
    flex: 1,
    minWidth: '180px',
    padding: '9px 14px',
    background: '#0f1f38',
    border: '1px solid #2d4a6e',
    borderRadius: '8px',
    fontSize: '0.88rem',
    fontFamily: "'SF Mono', 'Fira Code', monospace",
    '& input': {
      color: '#e2e8f0',
      padding: '6px 8px',
    },
    '& input::placeholder': {
      color: '#4a6080',
      opacity: 1,
    },
    '& input:focus': {
      borderColor: '#2563eb',
    },
  },
  container: {
    padding: '20px 32px',
  },
  // Stats bar
  statsBar: {
    display: 'flex',
    gap: '14px',
    padding: '12px 32px',
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  statPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 14px',
    borderRadius: '20px',
    fontSize: '0.78rem',
    fontWeight: 600,
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
  },
  statPillGreen: {
    background: '#f0fdf4',
    color: '#166534',
    borderColor: '#bbf7d0',
  },
  statPillAmber: {
    background: '#fffbeb',
    color: '#92400e',
    borderColor: '#fde68a',
  },
  statPillPurple: {
    background: '#faf5ff',
    color: '#6b21a8',
    borderColor: '#e9d5ff',
  },
  statPillStrong: {
    fontWeight: 800,
  },
  // Filter bar
  filterBar: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '12px 18px',
    borderBottom: '1px solid #f1f5f9',
  },
  filterSelect: {
    padding: '3px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.85rem',
    background: '#fff',
    color: '#1e293b',
    cursor: 'pointer',
  },
  selectPaper: {
    maxHeight: '300px',
    marginTop: '4px',
  },
  filterRight: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
  // Card
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    overflow: 'hidden',
  },
  hrtCard: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    overflow: 'hidden',
    marginTop: '20px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  // Buttons
  submitBtn: {
    marginLeft: 'auto',
    padding: '9px 22px',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#fff',
    borderRadius: '8px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  exportBtn: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #065f46, #059669)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '0.82rem',
    fontWeight: 700,
    '&:hover': {
      opacity: 0.88,
    },
  },
  exportHrtBtn: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #92400e, #d97706)',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '0.82rem',
    fontWeight: 700,
    '&:hover': {
      opacity: 0.88,
    },
  },
  // Toggle
  toggleGroup: {
    display: 'flex',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  toggleBtn: {
    padding: '7px 14px',
    border: 'none',
    background: '#fff',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s',
    '&:not(:last-child)': {
      borderRight: '1px solid #e2e8f0',
    },
  },
  toggleBtnActive: {
    background: '#1d4ed8',
    color: '#fff',
  },
  toggleBtnActiveTelem: {
    background: '#7c3aed',
    color: '#fff',
  },
  // TableData required styles
  apiHitsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8rem',
    '& thead th': {
      background: '#335977',
      padding: '11px 14px',
      textAlign: 'right',
      fontSize: '0.72rem',
      fontWeight: 700,
      color: '#FFE784',
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
      borderBottom: '2px solid #e5e7eb',
      whiteSpace: 'nowrap',
    },
    '& thead th:first-child': {
      textAlign: 'left',
      position: 'sticky',
      left: 0,
      zIndex: 2,
      minWidth: '220px',
    },
    '& tbody tr': {
      borderBottom: '1px solid #f1f5f9',
      transition: 'background 0.1s',
      '&:hover': {
        background: '#f8fafc',
      },
    },
    '& tbody td': {
      padding: '7px 10px',
      textAlign: 'right',
      verticalAlign: 'middle',
    },
    '& tbody td:first-child': {
      textAlign: 'left',
    },
  },
  hrtTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8rem',
    '& thead th': {
      background: '#335977',
      padding: '11px 14px',
      textAlign: 'right',
      fontSize: '0.72rem',
      fontWeight: 700,
      color: '#FFE784',
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
      borderBottom: '2px solid #e5e7eb',
      whiteSpace: 'nowrap',
    },
    '& thead th:first-child': {
      textAlign: 'left',
      position: 'sticky',
      left: 0,
      zIndex: 2,
      minWidth: '220px',
    },
    '& tbody tr': {
      borderBottom: '1px solid #f1f5f9',
      transition: 'background 0.1s',
      '&:hover': {
        background: '#f8fafc',
      },
    },
    '& tbody td': {
      padding: '7px 10px',
      textAlign: 'right',
      verticalAlign: 'middle',
    },
    '& tbody td:first-child': {
      textAlign: 'left',
    },
  },
  tableHeader: {
    fontWeight: 700,
    backgroundColor: '#335977',
    whiteSpace: 'nowrap',
  },
  isBold: {
    fontWeight: 700,
    color: '#FFFFFF',
  },
  sortContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '4px',
    cursor: 'pointer',
  },
  sortUp: {
    marginBottom: '-8px',
  },
  sortDown: {
    marginTop: '-4px',
  },
  active: {
    color: '#FFE784',
  },
  inactive: {
    color: '#b0bac6',
  },
  emptyTableContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    flexDirection: 'column',
  },
  titleWrapper: {
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
  },
  // Account cell
  accountCell: {
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    background: '#fff',
    zIndex: 1,
    borderRight: '2px solid #e2e8f0',
    minWidth: '220px',
  },
  acctName: {
    fontWeight: 600,
    fontSize: '0.82rem',
    color: '#1e293b',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  acctId: {
    fontSize: '0.65rem',
    color: '#94a3b8',
    fontFamily: "'SF Mono', 'Fira Code', monospace",
    marginTop: '1px',
  },
  // Data cells
  dataCell: {
    minWidth: '90px',
  },
  hitWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1px',
  },
  hitSuccess: {
    fontSize: '0.82rem',
    fontWeight: 700,
  },
  hitFourxx: {
    fontSize: '0.7rem',
    opacity: 0.72,
  },
  // Heat map - Success (blue)
  hm0: { background: '#f9fafb', '& $hitSuccess': { color: '#d1d5db' } },
  hm1: { background: '#eff6ff', '& $hitSuccess': { color: '#3b82f6' } },
  hm2: { background: '#dbeafe', '& $hitSuccess': { color: '#2563eb' } },
  hm3: { background: '#bfdbfe', '& $hitSuccess': { color: '#1d4ed8' } },
  hm4: { background: '#93c5fd', '& $hitSuccess': { color: '#1e3a8a' } },
  hm5: { background: '#3b82f6', '& $hitSuccess': { color: '#fff' }, '& $hitFourxx': { color: '#e0f2fe' } },
  hm6: { background: '#1d4ed8', '& $hitSuccess': { color: '#fff' }, '& $hitFourxx': { color: '#bfdbfe' } },
  // Heat map - 4xx (purple)
  hmt0: { background: '#f9fafb', '& $hitSuccess': { color: '#d1d5db' } },
  hmt1: { background: '#faf5ff', '& $hitSuccess': { color: '#9333ea' } },
  hmt2: { background: '#f3e8ff', '& $hitSuccess': { color: '#7c3aed' } },
  hmt3: { background: '#e9d5ff', '& $hitSuccess': { color: '#6d28d9' } },
  hmt4: { background: '#d8b4fe', '& $hitSuccess': { color: '#4c1d95' } },
  hmt5: { background: '#a855f7', '& $hitSuccess': { color: '#fff' }, '& $hitFourxx': { color: '#f3e8ff' } },
  hmt6: { background: '#7c3aed', '& $hitSuccess': { color: '#fff' }, '& $hitFourxx': { color: '#e9d5ff' } },
  // Heat map - HRT (amber)
  hmo0: { background: '#f9fafb', '& $hitSuccess': { color: '#d1d5db' } },
  hmo1: { background: '#fffbeb', '& $hitSuccess': { color: '#d97706' } },
  hmo2: { background: '#fef3c7', '& $hitSuccess': { color: '#b45309' } },
  hmo3: { background: '#fde68a', '& $hitSuccess': { color: '#92400e' } },
  hmo4: { background: '#fbbf24', '& $hitSuccess': { color: '#78350f' } },
  hmo5: { background: '#f59e0b', '& $hitSuccess': { color: '#fff' } },
  hmo6: { background: '#d97706', '& $hitSuccess': { color: '#fff' } },
  // Total columns
  totalSuccessCol: {
    borderLeft: '2px solid #bbf7d0',
    background: '#f0fdf4',
    minWidth: '90px',
  },
  totalFourxxCol: {
    borderLeft: '1px solid #e9d5ff',
    background: '#faf5ff',
    minWidth: '90px',
  },
  totalHrtCol: {
    borderLeft: '2px solid #fde68a',
    background: '#fffbeb',
    minWidth: '90px',
  },
  totalSuccessVal: {
    fontWeight: 700,
    color: '#166534',
    fontSize: '0.82rem',
  },
  totalFourxxVal: {
    fontWeight: 700,
    color: '#6d28d9',
    fontSize: '0.82rem',
  },
  totalHrtVal: {
    fontWeight: 700,
    color: '#92400e',
    fontSize: '0.82rem',
  },
  // Grand total row
  grandTotalCell: {
    background: '#fefce8',
    borderTop: '2px solid #fde68a',
    fontWeight: 700,
    color: '#92400e',
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
  },
  grandTotalAccountCell: {
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    background: '#fefce8',
    zIndex: 1,
    borderRight: '2px solid #e2e8f0',
    borderTop: '2px solid #fde68a',
    fontWeight: 700,
    color: '#92400e',
  },
  // HRT section title
  hrtSectionTitle: {
    padding: '14px 18px 0',
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#92400e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  hrtBadge: {
    background: '#fde68a',
    color: '#92400e',
    fontSize: '0.7rem',
    padding: '2px 10px',
    borderRadius: '12px',
  },
  hrtFilterBar: {
    display: 'flex',
    padding: '8px 18px',
    justifyContent: 'flex-end',
  },
  drillDownCellWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: '4px',
  },
  drillDownChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '20px',
    borderRadius: '4px',
    background: '#dbeafe',
    color: '#3b82f6',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.15s',
    '&:hover': {
      background: '#bfdbfe',
    },
  },
  drillDownChipPlaceholder: {
    display: 'inline-block',
    width: '22px',
    height: '20px',
    flexShrink: 0,
  },
  drillDownChipIcon: {
    fontSize: '0.9rem',
  },
  drillDownExpandCell: {
    padding: '0 !important',
    background: '#f5f7fa',
  },
  drillDownExpandInner: {
    padding: '16px 24px 20px',
  },
  drillDownHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '14px',
  },
  drillDownHeaderTitle: {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#0f3460',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  drillDownLoadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
  },
  drillDownTablesContainer: {
    width: '100%',
  },
  drillDownTableSection: {
    width: '100%',
  },
  drillDownSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  drillDownSectionTitle: {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#1f2937',
    textTransform: 'uppercase',
  },
  drillDownExportBtn: {
    fontSize: '0.7rem',
    color: '#059669',
    textTransform: 'none',
    fontWeight: 600,
    padding: '5px 12px',
    minWidth: 'auto',
    border: '1px solid #10b981',
    borderRadius: '6px',
    background: '#f0fdf4',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    marginLeft: '12px',
    '&:hover': {
      background: '#dcfce7',
      borderColor: '#059669',
    },
  },
  drillDownNestedTableWrap: {
    maxHeight: '450px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    background: '#fff',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
  drillDownNestedTable: {
    width: '100%',
    borderCollapse: 'collapse',
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  drillDownNestedTh: {
    background: '#f5f7fa',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    padding: '9px 14px',
    textAlign: 'left',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #e5e7eb',
    '&:nth-child(2)': {
      textAlign: 'right',
    },
    '&:nth-child(3)': {
      textAlign: 'right',
    },
  },
  drillDownNestedTd: {
    fontSize: '0.74rem',
    padding: '7px 14px',
    borderBottom: '1px solid #f3f4f6',
    color: '#1f2937',
    '&:nth-child(1)': {
      textAlign: 'left',
      color: '#6f7c91',
      fontWeight: 600,
    },
    '&:nth-child(2)': {
      textAlign: 'right',
      color: '#2457b8',
      fontWeight: 600,
    },
    '&:nth-child(3)': {
      textAlign: 'right',
      color: '#6d28d9',
      fontWeight: 600,
    },
  },
  drillDownEmpty: {
    textAlign: 'center',
    padding: '20px',
    color: '#9ca3af',
    fontSize: '0.85rem',
  },
  drillDownTotalRow: {
    background: '#fffbeb',
    borderTop: '2px solid #fbbf24',
    '&:hover': {
      background: '#fffbeb',
    },
  },
  drillDownTotalCell: {
    fontSize: '0.74rem',
    fontWeight: 600,
    padding: '9px 14px',
    '&:nth-child(1)': {
      textAlign: 'left',
      color: '#92400e',
    },
    '&:nth-child(2)': {
      textAlign: 'right',
      color: '#92400e',
    },
    '&:nth-child(3)': {
      textAlign: 'right',
      color: '#92400e',
    },
  },
  expandRow: {
    background: '#f8fafc',
  },
  download: {
    marginRight: '5px'
  }
}))

export { useStyles }
